// src/utils/describeCheck.js
//
// Plain-English rendering of a conformance Check (the schema in
// proxy-server/conformance/schemaContext.js — target + assert + severity).
//
// Why this exists: after "Compile criteria" the Launch page could name the
// checks a criterion mapped to but not SHOW what any of them asserts. A
// criterion came back "Met" with a check id and a confidence score, and the
// only way to find out what had actually been verified was to read the proxy's
// suite JSON on the droplet. That made a correct compile unauditable and a
// wrong one uneditable — the two complaints this module answers, together with
// GET /proxy/launches/<id>/checks (which supplies the definitions) and the
// spec/edit panel in LaunchPage.jsx.
//
// Every export is pure and NEVER throws: a malformed or partial check degrades
// to a readable fallback string, never an exception in a render path.

/** target.requestType / target.adCategory as a flat array of strings. */
function asList(v) {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim());
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
}

/** The dotted subject an assert reads: "custParams.hb_pb", "issues", "url.iu". */
// Sources that name an internal LOOKUP STRATEGY or container rather than a place
// a reviewer would recognise on the wire. `find` means "look anywhere — query,
// cust_params, or body" (it exists because DAI puts params in the POST body);
// `validData` is the proxy's own parsed view of a request. Neither says anything
// about the request itself, so they are dropped from the displayed subject:
// `find.ppid` reads as `ppid`, `validData.vastResponse.version` as
// `vastResponse.version`. Reported by a reviewer asking "why are you saying find.?"
// — same class as the check-slug jargon fixed in docs/checks-page-ux-spec.md §13.
//
// `url` and `body` are deliberately KEPT: which half of the request a param lives
// in is a real fact a reviewer may need, not an implementation detail.
const INTERNAL_SOURCES = new Set(['find', 'validData']);

// Sources whose internal key differs from the name on the wire.
const SOURCE_DISPLAY_NAME = { custParams: 'cust_params' };

export function assertSubject(assert) {
  try {
    const source = typeof assert?.source === 'string' ? assert.source : '';
    const param = (assert?.param !== undefined && assert?.param !== null && assert.param !== '')
      ? String(assert.param)
      : '';
    if (source && param) {
      if (INTERNAL_SOURCES.has(source)) return param;
      return `${SOURCE_DISPLAY_NAME[source] || source}.${param}`;
    }
    return SOURCE_DISPLAY_NAME[source] || source || param || 'the request';
  } catch {
    return 'the request';
  }
}

/** The operator half of an assert, as a predicate phrase. */
function describeOp(assert) {
  try {
    const op = typeof assert?.op === 'string' ? assert.op : '';
    switch (op) {
      case 'present': return 'is present';
      case 'absent': return Array.isArray(assert.allow) && assert.allow.length
        ? `has nothing outside the allowlist [${assert.allow.join(', ')}]`
        : 'is absent';
      case 'equals': return `equals ${JSON.stringify(assert.value)}`;
      case 'oneOf': return `is one of [${asList(assert.values).join(', ') || String(assert.values ?? '')}]`;
      case 'matches': return `matches /${assert.pattern}/`;
      case 'notMatches': return `does NOT match /${assert.pattern}/`;
      case 'gt': return `is greater than ${assert.value}`;
      case 'lt': return `is less than ${assert.value}`;
      case 'range': return `is within ${Array.isArray(assert.range) ? assert.range.join('–') : String(assert.range ?? '')}`;
      case 'inBucket': return assert.granularity
        ? `falls on a ${assert.granularity}-granularity price bucket`
        : 'falls on a configured price bucket';
      case 'validValue': return 'is a valid value for its vendor spec (format + meaning)';
      default: return op ? `${op} ${assert.value !== undefined ? JSON.stringify(assert.value) : ''}`.trim() : 'is checked';
    }
  } catch {
    return 'is checked';
  }
}

/**
 * isAbsenceCheck(check) -> boolean
 * True when the check proves something is NOT there (mode 'absent', or an
 * 'absent' operator). These are the checks whose evidence rows legitimately
 * read "nothing read" on EVERY row when they pass — the display distinction
 * the Launch page's evidence table needs so a clean absence check doesn't look
 * like a broken one.
 */
export function isAbsenceCheck(check) {
  try {
    const a = check?.assert || {};
    return a.mode === 'absent' || a.op === 'absent';
  } catch {
    return false;
  }
}

/**
 * describeAssert(check) -> string
 * The rule, as one sentence: mode (which rows must satisfy it) + subject +
 * operator.
 */
export function describeAssert(check) {
  try {
    const a = check?.assert;
    if (!a || typeof a !== 'object') return 'no assertion (malformed check)';
    const subject = assertSubject(a);
    const pred = describeOp(a);
    const mode = typeof a.mode === 'string' ? a.mode : 'each';
    switch (mode) {
      case 'each':
      case 'all':
        return `Every matched request: ${subject} ${pred}.`;
      case 'exists':
        return `At least one matched request has ${subject} ${pred}.`;
      case 'absent':
        return `NO matched request may have ${subject} ${pred}.`;
      case 'count':
        return `Counts matched requests where ${subject} ${pred}.`;
      case 'ratio':
        return `Measures the share of matched requests where ${subject} ${pred}.`;
      case 'noDuplicates':
        return `No two matched requests share the same ${subject}.`;
      case 'minInterval':
        return `Matched requests must be spaced apart in time (${subject}).`;
      case 'uniform':
        return `All matched requests must carry the SAME ${subject}.`;
      default:
        return `${mode}: ${subject} ${pred}.`;
    }
  } catch {
    return 'no assertion (malformed check)';
  }
}

/**
 * describeScope(check) -> string
 * WHICH requests the check grades. The load-bearing half: a check reporting
 * "Pass" tells a tester nothing until they know it looked at Media.net's
 * Prebid Server and not every prebid request on the wire (requestType
 * classifies integration shape, so host is what separates two vendors — see
 * proxy-server/conformance/vendorScope.js).
 */
export function describeScope(check) {
  try {
    const t = check?.target;
    if (!t || typeof t !== 'object') return 'every request in scope (no target — matches the whole session)';
    if (t.entity) return `derived ${t.entity} entities`;
    const parts = [];
    const types = asList(t.requestType);
    const cats = asList(t.adCategory);
    if (types.length) parts.push(`${types.join(' / ')} requests`);
    if (cats.length) parts.push(`${cats.join(' / ')} traffic`);
    const up = t.urlPattern;
    if (up && typeof up === 'object') {
      const q = typeof up.query === 'string' && up.query ? `?${up.query}` : '';
      parts.push(`requests whose URL matches ${asList(up.host).join(', ')}${typeof up.path === 'string' ? up.path : ''}${q}`);
    }
    if (!parts.length) parts.push('every request in scope');
    const hosts = asList(t.host);
    let s = parts.join(' + ');
    if (hosts.length) s += ` on ${hosts.join(', ')}`;
    else if (types.length || cats.length) s += ' (any host)';
    const preds = (Array.isArray(t.where) ? t.where : [t.where]).filter((p) => p && typeof p === 'object');
    preds.forEach((p, i) => {
      s += `${i === 0 ? ', only' : ' and only'} where ${assertSubject(p)} ${describeOp(p)}`;
    });
    return s;
  } catch {
    return 'every request in scope';
  }
}

/**
 * describeCheck(check) -> { id, description, displayName, statement, why, fix, scope, rule, severity, absence }
 * Everything the spec panel renders for one check. Never throws.
 */
export function describeCheck(check) {
  return {
    id: (check && typeof check.id === 'string') ? check.id : '(no id)',
    description: (check && typeof check.description === 'string') ? check.description : '',
    // Reader-facing rule text (adtech-baseline rewrite, 2026-10-16): the rule as
    // a one-sentence requirement, why it matters, and what to do. All optional
    // — authored / user checks may carry only `description`.
    displayName: (check && typeof check.displayName === 'string') ? check.displayName : '',
    statement: (check && typeof check.statement === 'string') ? check.statement : '',
    why: (check && typeof check.why === 'string') ? check.why : '',
    fix: (check && typeof check.fix === 'string') ? check.fix : '',
    scope: describeScope(check),
    rule: describeAssert(check),
    severity: (check && typeof check.severity === 'string') ? check.severity : 'warn',
    absence: isAbsenceCheck(check),
  };
}
